import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { BkPage, Card, SmallBtn, fmtMoney } from "@/components/bookkeeping/BkPage";

const input = "rounded-lg border border-[#333] bg-[#0F0F0F] px-3 py-2 text-sm text-white";
const BLANK = { name: "", payee_contains: "", bank_account_id: "", direction: "out", amount_min: "", amount_max: "", action: "categorize", qbo_account_id: "", vendor_name: "", priority: 100 };

const toBody = (f, entityId) => ({ ...f, entity_id: entityId, direction: f.direction || null, bank_account_id: f.bank_account_id || null,
  amount_min: f.amount_min === "" ? null : Number(f.amount_min), amount_max: f.amount_max === "" ? null : Number(f.amount_max), priority: Number(f.priority) || 100 });

const Field = ({ label, children }) => <label className="flex flex-col gap-1 text-[11px] font-bold uppercase tracking-wider text-neutral-500">{label}{children}</label>;

const RuleForm = ({ entity, banks, cats, onSaved }) => {
  const [f, setF] = useState(BLANK);
  const [preview, setPreview] = useState(null);
  const set = (k) => (e) => { setPreview(null); setF({ ...f, [k]: e.target.value }); };
  const list = f.direction === "in" ? cats.in : f.direction === "out" ? cats.out : [...cats.out, ...cats.in];
  const call = async (path) => api(path, { method: "POST", body: toBody(f, entity.id) });
  const doPreview = async () => { try { setPreview(await call("/bookkeeping/rules/preview")); } catch (e) { toast.error(e.message); } };
  const save = async () => {
    try { const r = await call("/bookkeeping/rules"); toast.success(`Rule saved${r.applied_now ? `; applied to ${r.applied_now} waiting transaction(s)` : ""}`); setF(BLANK); setPreview(null); onSaved(); } catch (e) { toast.error(e.message); }
  };
  return (
    <Card title="NEW RULE" testId="bk-rule-form">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <Field label="Name"><input data-testid="bk-rule-name" value={f.name} onChange={set("name")} placeholder="e.g. Fuel" className={input} /></Field>
        <Field label="Payee or description contains"><input data-testid="bk-rule-payee" value={f.payee_contains} onChange={set("payee_contains")} placeholder="e.g. SHELL" className={input} /></Field>
        <Field label="Bank account or card">
          <select data-testid="bk-rule-bank" value={f.bank_account_id} onChange={set("bank_account_id")} className={input}>
            <option value="">Any account</option>{banks.map((b) => <option key={b.id} value={b.id}>{b.name} ••{b.mask}</option>)}
          </select>
        </Field>
        <Field label="Money in or out">
          <select data-testid="bk-rule-direction" value={f.direction} onChange={set("direction")} className={input}>
            <option value="out">Money out</option><option value="in">Money in</option><option value="">Either</option>
          </select>
        </Field>
        <Field label="Amount from ($)"><input data-testid="bk-rule-min" type="number" min="0" step="0.01" value={f.amount_min} onChange={set("amount_min")} className={input} /></Field>
        <Field label="Amount up to ($)"><input data-testid="bk-rule-max" type="number" min="0" step="0.01" value={f.amount_max} onChange={set("amount_max")} className={input} /></Field>
        <Field label="Then">
          <select data-testid="bk-rule-action" value={f.action} onChange={set("action")} className={input}>
            <option value="categorize">Categorize and post to QuickBooks</option><option value="exclude">Exclude (personal / not business)</option>
          </select>
        </Field>
        {f.action === "categorize" && <>
          <Field label="QuickBooks category">
            <select data-testid="bk-rule-category" value={f.qbo_account_id} onChange={set("qbo_account_id")} className={input}>
              <option value="">Pick a category…</option>{list.map((c) => <option key={c.qbo_id} value={c.qbo_id}>{c.name}</option>)}
            </select>
          </Field>
          <Field label="Vendor (created if new)"><input data-testid="bk-rule-vendor" value={f.vendor_name} onChange={set("vendor_name")} placeholder="e.g. Shell" className={input} /></Field>
        </>}
        <Field label="Priority (lower runs first)"><input data-testid="bk-rule-priority" type="number" value={f.priority} onChange={set("priority")} className={input} /></Field>
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <SmallBtn onClick={doPreview} data-testid="bk-rule-preview">Preview matches</SmallBtn>
        <SmallBtn primary onClick={save} data-testid="bk-rule-save">Save rule</SmallBtn>
        {preview && <span className="text-xs text-neutral-300" data-testid="bk-rule-preview-count">Matches {preview.count} transaction(s){preview.sample.length ? `: ${preview.sample.slice(0, 3).map((s) => `${s.merchant_name || s.description} ${fmtMoney(s.amount)}`).join(", ")}` : ""}</span>}
      </div>
    </Card>
  );
};

const summary = (r, banks) => [r.payee_contains && `contains “${r.payee_contains}”`, r.direction && (r.direction === "out" ? "money out" : "money in"),
  r.bank_account_id && (banks.find((b) => b.id === r.bank_account_id)?.name || "one account"),
  (r.amount_min != null || r.amount_max != null) && `${r.amount_min != null ? fmtMoney(r.amount_min) : "$0"}–${r.amount_max != null ? fmtMoney(r.amount_max) : "any"}`].filter(Boolean).join(" · ");

const RuleList = ({ rules, banks, reload }) => {
  const call = async (path, method, body) => { try { await api(path, { method, body }); reload(); } catch (e) { toast.error(e.message); } };
  return (
    <Card title={`RULES · ${rules.length}`} testId="bk-rules-list">
      {rules.length === 0 ? <p className="text-sm text-neutral-500">No rules yet. Tick “Always do this” in the review queue or add one above.</p> : (
        <div className="divide-y divide-[#1f1f1f]">
          {rules.map((r) => (
            <div key={r.id} className={`flex flex-wrap items-center gap-3 py-2 text-sm ${r.active ? "" : "opacity-50"}`} data-testid={`bk-rule-${r.id}`}>
              <span className="w-10 text-xs text-neutral-500">#{r.priority}</span>
              <span className="min-w-0 flex-1 text-white">{r.name}<span className="block text-xs text-neutral-500">{summary(r, banks)} → {r.action === "exclude" ? "Exclude" : `${r.account_name}${r.vendor_name ? ` · ${r.vendor_name}` : ""}`}</span></span>
              <span className="text-xs text-neutral-400">used {r.times_applied || 0}×</span>
              <SmallBtn onClick={() => call(`/bookkeeping/rules/${r.id}`, "PATCH", { active: !r.active })} data-testid={`bk-rule-toggle-${r.id}`}>{r.active ? "Pause" : "Resume"}</SmallBtn>
              <SmallBtn onClick={() => window.confirm("Delete this rule? Transactions it already categorized stay as they are.") && call(`/bookkeeping/rules/${r.id}`, "DELETE")} data-testid={`bk-rule-delete-${r.id}`}>Delete</SmallBtn>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};

const Body = ({ entity }) => {
  const [d, setD] = useState(null);
  const load = useCallback(async () => {
    const q = `entity_id=${entity.id}`;
    const [rules, cats, conn] = await Promise.all([api(`/bookkeeping/rules?${q}`), api(`/bookkeeping/categories?${q}`), api(`/bookkeeping/connections?${q}`)]);
    setD({ rules, cats, banks: conn.bank_accounts });
  }, [entity.id]);
  useEffect(() => { setD(null); load().catch((e) => toast.error(e.message)); }, [load]);
  if (!d) return null;
  return (
    <>
      <RuleForm entity={entity} banks={d.banks} cats={d.cats} onSaved={load} />
      <RuleList rules={d.rules} banks={d.banks} reload={load} />
    </>
  );
};

export default function Rules() {
  return <BkPage title="Rules" testId="bookkeeping-rules">{({ entity }) => <Body entity={entity} />}</BkPage>;
}
